import * as React from 'react'
import { useState } from 'react'
import { styled } from '@/src/stitches.config'
import {
    Dialog,
    DialogClose,
    DialogContent,
} from '@/src/common/components/elements/Modals/Modal'
import HeadlineEyebrow from '@/src/common/components/marketing/headlines/HeadlineEyebrow'
import HtmlSelectInput from '@/src/common/components/elements/Forms/HtmlSelectInput'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark'
import { IBACPredesign } from '@/src/modules/build-a-card/types'
import { assembleCategoriesAndFilterDesigns } from '@/src/common/utilities/assembleCategoriesAndFilterDesigns'
import Image from 'next/image'
import { PREDESIGN_IMAGE_TYPE } from '@/src/modules/build-a-card/constants'
import { usePartnerTheme } from '@/src/common/contexts/PartnerThemeContext'

export interface IPredesignsModal {
    isOpen: boolean
    setIsOpen: Function
    setDesignImage: Function
    setCustomDesignObject: Function
    setPredesignUuid: Function
    predesignUuid: string
    predesigns: IBACPredesign[]
    headlineLabel?: string
    setDesignName: Function
    setSelectedImageType: Function
}

const ModalHeaderWrapper = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    mt: 20,
    '@md': {
        flexDirection: 'row',
    },
})
const IconButton = styled('button', {
    all: 'unset',
    fontFamily: 'inherit',
    borderRadius: '100%',
    height: 25,
    width: 25,
    fontSize: 25,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '$dark',
    position: 'absolute',
    top: 10,
    right: 10,
    '&:hover': { backgroundColor: '$light' },
})
const SelectInputWrapper = styled('div', {
    width: '288px',
    maxWidth: '100%',
    mt: 20,
    '@md': {
        mt: 0,
        pr: 20,
    },
})
const DesignsWrapper = styled('div', {
    width: '100%',
    display: 'flex',
    flexFlow: 'row wrap',
    jc: 'space-between',
    my: 20,
    gap: 15,
    '@sm': {
        jc: 'start',
        gap: 15,
    },
})
const Design = styled(Image, {
    borderRadius: 10,
    br: 10,
    width: '47%',
    height: 'auto',
    objectFit: 'cover',
    aspectRatio: '3/2',
    boxShadow: 'grey 0px 1px 5px -1px',
    outlineColor: '$primary',
    '@sm': {
        width: '30%',
    },
    '@md': {
        width: '18%',
        maxWidth: 200,
    },
    '@lg': {
        width: '15%',
    },
    variants: {
        selected: {
            true: {
                boxShadow: '0 0 0 3px $primary',
            },
        },
    },
})

export const PredesignsModal: React.FC<IPredesignsModal> = ({
    isOpen,
    setIsOpen,
    setDesignImage,
    setCustomDesignObject,
    setPredesignUuid,
    predesignUuid,
    predesigns,
    headlineLabel,
    setDesignName,
    setSelectedImageType,
}) => {
    const [selectedCategory, setSelectedCategory] = useState('empty')
    const { categories, filteredDesigns } = assembleCategoriesAndFilterDesigns({
        items: predesigns,
        selectedCategory: selectedCategory,
    })
    const partnerTheme = usePartnerTheme()

    return (
        <Dialog open={isOpen}>
            <DialogContent
                className={partnerTheme}
                width={'large'}
                height={'90vh'}
                data-testid="predesign_modal"
                onPointerDownOutside={() => setIsOpen(false)}
                onKeyDown={(e: any) => {
                    if (e.code === 'Escape') {
                        e.preventDefault()
                        setIsOpen(false)
                    }
                }}
            >
                <ModalHeaderWrapper>
                    <div>
                        <HeadlineEyebrow
                            label={headlineLabel ?? 'card designs'}
                        />
                    </div>
                    <SelectInputWrapper>
                        <HtmlSelectInput
                            label={'Category'}
                            emptyOptionLabel={'All'}
                            options={categories}
                            value={selectedCategory}
                            onValueChange={(value: any) => {
                                setSelectedCategory(value)
                            }}
                        />
                    </SelectInputWrapper>
                </ModalHeaderWrapper>
                <DesignsWrapper>
                    {filteredDesigns.map((pd, index) => (
                        <Design
                            key={pd.uuid}
                            width={200}
                            height={140}
                            alt={'predesign-image'}
                            selected={pd.uuid === predesignUuid}
                            onClick={() => {
                                setDesignImage(pd.preview_image_url)
                                setDesignName(pd.design_name)
                                setPredesignUuid(pd.uuid)
                                setCustomDesignObject({}) // Clear the custom design data
                                setIsOpen(false)
                                setSelectedImageType(PREDESIGN_IMAGE_TYPE)
                            }}
                            onKeyDown={(e) => {
                                if (e.code === 'Enter') {
                                    e.preventDefault()
                                    setDesignImage(pd.preview_image_url)
                                    setDesignName(pd.design_name)
                                    setPredesignUuid(pd.uuid)
                                    setCustomDesignObject({}) // Clear the custom design data
                                    setIsOpen(false)
                                }
                            }}
                            data-testid={`predesign-${index}`}
                            tabIndex={0}
                            src={pd.preview_image_url}
                        />
                    ))}
                </DesignsWrapper>
                <DialogClose asChild>
                    <IconButton
                        aria-label="Close"
                        onClick={() => {
                            setIsOpen(false)
                            setSelectedCategory('empty')
                        }}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </IconButton>
                </DialogClose>
            </DialogContent>
        </Dialog>
    )
}
